// @ts-nocheck
/**
 * Session Management with Supabase Auth
 * SECURITY: Tokens managed by Supabase, roles from database
 */

import { create } from "zustand";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

// ✅ Cache para evitar re-render ao receber sessão duplicada
let lastSessionUserId: string | null = null;

export type UserRole = "admin" | "gestor" | "funcionario";

export interface User {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  role?: UserRole; // Loaded asynchronously from database
}

interface SessionState {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoadingRole: boolean;
  authIsReady: boolean;
  lastActivity: number;
  
  setSupabaseSession: (session: Session | null) => Promise<void>;
  loadUserRole: (userId: string) => Promise<UserRole | null>;
  clearSession: () => void;
  updateActivity: () => void;
  isSessionExpired: () => boolean;
}

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  session: null,
  isAuthenticated: false,
  isLoadingRole: false,
  authIsReady: false,
  lastActivity: Date.now(),

  setSupabaseSession: async (session) => {
    // ✅ Ignorar sessão duplicada (previne re-render desnecessário)
    if (session?.user?.id === lastSessionUserId) {
      console.log('⏭️ Sessão duplicada ignorada:', session.user.id);
      return;
    }

    lastSessionUserId = session?.user?.id || null;

    if (!session) {
      lastSessionUserId = null; // Reset no logout
      set({
        user: null,
        session: null,
        isAuthenticated: false,
        isLoadingRole: false,
        authIsReady: true,
        lastActivity: Date.now(),
      });
      return;
    }

    // Set authenticated IMMEDIATELY for instant UI transition
    set({
      session,
      user: {
        id: session.user.id,
        email: session.user.email!,
        name: session.user.email?.split('@')[0]?.trim() || 'Usuário',
      },
      isAuthenticated: true,
      isLoadingRole: true,
      authIsReady: false,
      lastActivity: Date.now(),
    });

    // ⬇️ ADD: LOG de sessão inicial
    console.log("🔐 SESSION:init", {
      isAuthenticated: true,
      isLoadingRole: true,
      userId: session?.user?.id,
      email: session?.user?.email,
      tempName: session.user.email?.split('@')[0] || 'Usuário'
    });

    // Safety timeout: force isLoadingRole = false after 3 seconds
    const timeoutId = setTimeout(() => {
      const currentState = get();
      if (currentState.isLoadingRole) {
        set({ isLoadingRole: false });
      }
    }, 3000);

    try {
      // Load profile and role in background
      const [profileResult, roleResult] = await Promise.all([
        supabase
          .from("profiles")
          .select("name, avatar_url")
          .eq("id", session.user.id)
          .maybeSingle(),
        get().loadUserRole(session.user.id),
      ]);

      clearTimeout(timeoutId);

      const profile = profileResult.data;
      
      set({
        user: {
          id: session.user.id,
          email: session.user.email!,
          name: profile?.name || session.user.email!,
          avatar_url: profile?.avatar_url,
          role: roleResult || undefined,
        },
        isLoadingRole: false,
        authIsReady: true,
      });
    } catch (error) {
      console.error("Error loading user data:", error);
      clearTimeout(timeoutId);
      set({ isLoadingRole: false, authIsReady: true });
    }
  },

  loadUserRole: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("tenant_user_roles")
        .select("role")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;
      return data?.role as UserRole;
    } catch (error) {
      console.error("Error loading role:", error);
      return null;
    }
  },

  clearSession: () => {
    set({
      user: null,
      session: null,
      isAuthenticated: false,
      lastActivity: Date.now(),
    });
  },

  updateActivity: () => {
    set({ lastActivity: Date.now() });
  },

  isSessionExpired: () => {
    const { lastActivity } = get();
    return Date.now() - lastActivity > SESSION_TIMEOUT;
  },
}));

// Idle Watch com reset de atividade
let idleTimer: number | null = null;
const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30min

export function startIdleWatch() {
  stopIdleWatch();
  
  const reset = () => {
    if (idleTimer) window.clearTimeout(idleTimer);
    
    idleTimer = window.setTimeout(async () => {
      const { isAuthenticated, clearSession } = useSession.getState();
      
      // Não deslogar se aba invisível (previne logout ao voltar)
      if (document.visibilityState !== 'visible') return;
      if (!isAuthenticated) return;
      
      console.warn("⏱️ IdleWatch: deslogando por inatividade");
      
      // Audit log
      const auditId = localStorage.getItem('current_audit_id');
      if (auditId) {
        try {
          await supabase
            .from('session_audit')
            .update({ 
              logout_at: new Date().toISOString(),
              status: 'forced_logout',
              failure_reason: 'Inatividade por 30 minutos'
            })
            .eq('id', auditId);
          localStorage.removeItem('current_audit_id');
        } catch (error) {
          console.error('Error logging forced logout:', error);
        }
      }
      
      try {
        await supabase.auth.signOut();
      } finally {
        clearSession();
      }
    }, IDLE_LIMIT_MS);
  };
  
  // Eventos que resetam o timer
  const events = ['mousemove', 'keydown', 'click', 'scroll', 'focus', 'touchstart'];
  events.forEach(evt => window.addEventListener(evt, reset, { passive: true }));
  
  reset(); // Inicia timer
  
  // Retorna cleanup function
  return () => {
    stopIdleWatch();
    events.forEach(evt => window.removeEventListener(evt, reset));
  };
}

export function stopIdleWatch() {
  if (idleTimer) {
    window.clearTimeout(idleTimer);
    idleTimer = null;
  }
}
